/**
 * Centralized structured logger for the application.
 *
 * Uses JSON format in production for log aggregation tools,
 * and human-readable format in development.
 * Error/fatal levels automatically forward to Sentry.
 */

import * as Sentry from '@sentry/nextjs'

type LogLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'fatal'

interface LogContext {
  [key: string]: unknown
}

// Determine environment
const isDevelopment = process.env.NODE_ENV === 'development'
const logLevel = (process.env.LOG_LEVEL as LogLevel) || (isDevelopment ? 'debug' : 'info')

// Log level hierarchy
const LOG_LEVELS: Record<LogLevel, number> = {
  trace: 0,
  debug: 1,
  info: 2,
  warn: 3,
  error: 4,
  fatal: 5,
}

// Sensitive fields to redact from logs
const REDACT_FIELDS = [
  'password',
  'token',
  'accessToken',
  'refreshToken',
  'apiKey',
  'secret',
  'authorization',
  'cookie',
]

// PII redacted by EXACT key match (case-insensitive). Substring matching is
// wrong here: `recipients` is a fan-out size, `recipientCount`/`recipientIds`
// /`emailsSent`/`emailsFailed`/`emailId`/`emailFrequency` are metrics, and
// redacting them would blind the large-fan-out alert in
// lib/notifications/channels/email.ts.
const REDACT_FIELDS_EXACT = [
  'email',
  'emails',
  'emailaddress',
  'email_address',
  'useremail',
  'user_email',
  'recipient',
  'recipientemail',
  'recipient_email',
  'phone',
  'phonenumber',
  'phone_number',
]

function shouldLog(level: LogLevel): boolean {
  return LOG_LEVELS[level] >= LOG_LEVELS[logLevel]
}

function redactSensitiveData(obj: LogContext): LogContext {
  const redacted: LogContext = {}
  for (const [key, value] of Object.entries(obj)) {
    const lowerKey = key.toLowerCase()
    if (
      REDACT_FIELDS_EXACT.includes(lowerKey) ||
      REDACT_FIELDS.some((field) => lowerKey.includes(field.toLowerCase()))
    ) {
      redacted[key] = '[REDACTED]'
    } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      redacted[key] = redactSensitiveData(value as LogContext)
    } else {
      redacted[key] = value
    }
  }
  return redacted
}

function formatMessage(level: LogLevel, context: LogContext, message: string): string {
  const timestamp = new Date().toISOString()
  const safeContext = redactSensitiveData(context)

  if (isDevelopment) {
    // Human-readable format for development
    const contextStr = Object.keys(safeContext).length > 0 ? ` ${JSON.stringify(safeContext)}` : ''
    return `[${timestamp.slice(11, 19)}] ${level.toUpperCase().padEnd(5)} ${message}${contextStr}`
  } else {
    // JSON format for production (structured logging)
    return JSON.stringify({
      timestamp,
      level,
      message,
      ...safeContext,
      env: process.env.NODE_ENV || 'development',
    })
  }
}

function log(level: LogLevel, context: LogContext, message: string): void {
  if (!shouldLog(level)) return

  const formatted = formatMessage(level, context, message)

  switch (level) {
    case 'trace':
    case 'debug':
      console.debug(formatted)
      break
    case 'info':
      console.info(formatted)
      break
    case 'warn':
      console.warn(formatted)
      break
    case 'error':
    case 'fatal':
      console.error(formatted)
      break
  }

  // Skip Sentry for transient/expected errors that create noise
  const errorStr =
    context.error instanceof Error
      ? context.error.message
      : typeof context.error === 'string'
        ? context.error
        : ''
  const isTransientNoise =
    /rate_limit_exceeded|429|too many requests/i.test(errorStr) ||
    /rate_limit_exceeded|429|too many requests/i.test(message) ||
    /Upstream service returned HTML error response/i.test(errorStr) ||
    /Upstream service returned HTML error response/i.test(message) ||
    /^\s*<(!doctype\b|html\b)/i.test(errorStr)

  // Forward error/fatal to Sentry for alerting (skip transient noise)
  if ((level === 'error' || level === 'fatal') && !isTransientNoise) {
    try {
      const safeContext = redactSensitiveData(context)
      const errorObj = context.error
      const sentryLevel = level === 'fatal' ? 'fatal' : 'error'
      const sentryTags = { domain: (safeContext.domain as string) || 'unknown' }
      if (errorObj instanceof Error) {
        Sentry.captureException(errorObj, {
          level: sentryLevel,
          tags: sentryTags,
          extra: { ...safeContext, message },
        })
      } else if (typeof errorObj === 'string') {
        Sentry.captureException(new Error(errorObj), {
          level: sentryLevel,
          tags: sentryTags,
          extra: { ...safeContext, message },
        })
      } else {
        Sentry.captureMessage(message, {
          level: sentryLevel,
          tags: sentryTags,
          extra: safeContext,
        })
      }
    } catch {
      // Sentry forwarding must never break logging
    }
  }
}

interface Logger {
  trace: (context: LogContext, message: string) => void
  debug: (context: LogContext, message: string) => void
  info: (context: LogContext, message: string) => void
  warn: (context: LogContext, message: string) => void
  error: (context: LogContext, message: string) => void
  fatal: (context: LogContext, message: string) => void
  child: (baseContext: LogContext) => Logger
}

function createLogger(baseContext: LogContext = {}): Logger {
  return {
    trace: (context, message) => log('trace', { ...baseContext, ...context }, message),
    debug: (context, message) => log('debug', { ...baseContext, ...context }, message),
    info: (context, message) => log('info', { ...baseContext, ...context }, message),
    warn: (context, message) => log('warn', { ...baseContext, ...context }, message),
    error: (context, message) => log('error', { ...baseContext, ...context }, message),
    fatal: (context, message) => log('fatal', { ...baseContext, ...context }, message),
    child: (childContext) => createLogger({ ...baseContext, ...childContext }),
  }
}

/**
 * Create a child logger for a specific domain
 * @param domain - The domain name (e.g., 'auth', 'billing', 'email')
 * @returns A child logger with the domain context
 */
export function createChildLogger(domain: string): Logger {
  return createLogger({ domain })
}

/**
 * Create a request-scoped logger with request context
 * @param requestId - Unique request identifier
 * @param userId - Optional user ID
 * @param orgId - Optional organization ID
 * @returns A child logger with request context
 */
export function createRequestLogger(requestId: string, userId?: string, orgId?: string): Logger {
  return createLogger({
    requestId,
    ...(userId && { userId }),
    ...(orgId && { orgId }),
  })
}

// Base logger instance
export const logger = createLogger()

// Pre-configured domain loggers for common use cases
export const authLogger = createChildLogger('auth')
export const billingLogger = createChildLogger('billing')
export const emailLogger = createChildLogger('email')
export const webhookLogger = createChildLogger('webhook')
export const cronLogger = createChildLogger('cron')
export const apiLogger = createChildLogger('api')
export const dashboardLogger = createChildLogger('dashboard')
export const aiLogger = createChildLogger('ai')

export default logger
